import { CreditCard, Eye, Pencil, SearchX, UserPlus } from "lucide-react";
import { useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAvatar } from "@/shared/components/data-display/ColocaAvatar";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { ClientListItem } from "../types/clients-api.types";
import { clientDisplayName } from "../utils/normalizeClient";
import { CountryLabel } from "./CountryLabel";
import { KycStatusPill } from "./KycStatusPill";
import { TablePaginationFooter } from "./TablePaginationFooter";

export interface OrganizationClientsTableProps {
  rows: ClientListItem[];
  isLoading: boolean;
  hasActiveFilters: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onViewClient?: (clientId: string) => void;
  onEditClient?: (clientId: string) => void;
  onViewAccounts?: (clientId: string, displayName: string) => void;
  onClearFilters: () => void;
  onCreateClient?: () => void;
}

/** Level 2 of the master-detail: server-paginated clients of one organization (or the org user's own list). */
export function OrganizationClientsTable({
  rows,
  isLoading,
  hasActiveFilters,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  onViewClient,
  onEditClient,
  onViewAccounts,
  onClearFilters,
  onCreateClient,
}: OrganizationClientsTableProps) {
  const { t } = useTranslations("clients");

  const columns = useMemo<ColocaDataTableColumn<ClientListItem>[]>(
    () => [
      {
        id: "type",
        header: t("table_th_type"),
        cell: (row) => (
          <ColocaBadge variant={row.type === "business" ? "secondary" : "default"}>
            {row.type === "business" ? t("table_type_empresa") : t("table_type_individual")}
          </ColocaBadge>
        ),
      },
      {
        id: "name",
        header: t("table_th_name"),
        cell: (row) => {
          const name = clientDisplayName(row);
          const document =
            row.identification_type && row.identification_number
              ? `${row.identification_type} ${row.identification_number}`
              : null;
          return (
            <div className="flex items-center gap-3">
              <ColocaAvatar name={name} size="sm" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{name}</p>
                {document && <p className="truncate text-xs text-muted-foreground">{document}</p>}
              </div>
            </div>
          );
        },
        cellClassName: "max-w-60",
      },
      {
        id: "email",
        header: t("table_th_email"),
        cell: (row) => (
          <span className="block truncate text-sm text-muted-foreground" title={row.email ?? undefined}>
            {row.email ?? "—"}
          </span>
        ),
        cellClassName: "max-w-50",
      },
      {
        id: "country",
        header: t("table_th_country"),
        cell: (row) => <CountryLabel code={row.country} />,
      },
      {
        id: "kyc",
        header: t("table_th_kyc"),
        cell: (row) => <KycStatusPill status={row.kyc_status} />,
      },
      {
        id: "created",
        header: t("table_th_created"),
        cell: (row) => (
          <span className="whitespace-nowrap text-sm text-muted-foreground tabular-nums">
            {formatDateTime(row.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: <span className="sr-only">{t("table_th_actions")}</span>,
        cell: (row) => (
          <div className="flex items-center justify-end gap-1 opacity-60 transition-opacity group-hover/row:opacity-100">
            {onViewClient && (
              <ColocaIconButton
                variant="ghost"
                size="sm"
                icon={Eye}
                aria-label={t("action_view")}
                title={t("action_view")}
                onClick={() => onViewClient(row.id)}
              />
            )}
            {onEditClient && (
              <ColocaIconButton
                variant="ghost"
                size="sm"
                icon={Pencil}
                aria-label={t("action_edit")}
                title={t("action_edit")}
                onClick={() => onEditClient(row.id)}
              />
            )}
            {onViewAccounts && (
              <ColocaIconButton
                variant="ghost"
                size="sm"
                icon={CreditCard}
                aria-label={t("action_accounts_aria")}
                title={t("action_accounts_aria")}
                onClick={() => onViewAccounts(row.id, clientDisplayName(row))}
              />
            )}
          </div>
        ),
        headerClassName: "text-right",
      },
    ],
    [t, onViewClient, onEditClient, onViewAccounts],
  );

  return (
    <ColocaDataTable
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      loading={isLoading}
      skeletonRows={Math.min(pageSize, 8)}
      minWidth={900}
      empty={
        hasActiveFilters ? (
          <ColocaEmpty
            icon={SearchX}
            title={t("empty_no_results_title")}
            description={t("empty_no_results_description")}
            surface="card"
          >
            <ColocaButton variant="default" onClick={onClearFilters}>
              {t("filters_button_clear")}
            </ColocaButton>
          </ColocaEmpty>
        ) : (
          <ColocaEmpty
            icon={UserPlus}
            title={t("empty_clients_title")}
            description={t("empty_clients_description")}
            surface="card"
          >
            {onCreateClient && (
              <ColocaButton variant="primary" onClick={onCreateClient}>
                {t("button_create_client")}
              </ColocaButton>
            )}
          </ColocaEmpty>
        )
      }
      footer={
        <TablePaginationFooter
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          summaryKey="clients_pagination_summary"
        />
      }
    />
  );
}
